import React, { useState, useEffect, useRef } from "react";
import styles from "./InterestingLanding.module.css";

const InterestingLandingPage = () => {
  // Helper function for image paths
  const getImagePath = (imageName) =>
    process.env.PUBLIC_URL + "/images/" + imageName;
  // Define tabData first to avoid reference errors
  const tabData = [
    {
      title: "Start with Referrals",
      subtitle: "Share loan opportunities and earn from quick\npayouts",
      cardTitle: "Anyone who wants to refer can start from here",
      bullets: [
        "Sign up to share a referral.",
        "Our loan agent handles all the paperwork and processing.",
        "If the loan is approved you receive a referral fee",
      ],
      iconText:
        "Perfect if you're just starting out or want extra income without the complexity.",
      image: "/images/Rectangle 83.svg",
    },
    {
      title: "Get Hands-On",
      subtitle: "Share loan opportunities and earn from quick\npayouts",
      cardTitle:
        "If you're a loan agent, DSA, or real estate pro who likes saying 'approved' more than 'pending.'",
      bullets: [
        "Access guided tools to understand borrower profiles.",
        "Match customers with the right banks & products easily (60+ banks)",
        "Learn by doing — build confidence while closing real cases.",
      ],
      iconText: "Ideal if you're curious and want to become more hands-on",
      image: "/images/Rectangle 84.svg",
    },
    {
      title: "Scale with Marketplace",
      subtitle: "Share loan opportunities and earn from quick\npayouts",
      cardTitle: "If you are ambitious to expand your loan business at scale",
      bullets: [
        "Access our loan marketplace and connect with banks, NBFCs",
        "Expand loan offerings to your customer base and earn revenue from commissions",
        "Own the your customer relationship while building long-term trust.",
      ],
      iconText:
        "Perfect if you want to run loan origination as a serious business, with Interesting powering you in the background",
      image: "/images/Rectangle 85.svg",
    },
    {
      title: "Spread Financial wellness",
      subtitle: "Share loan opportunities and earn from quick\npayouts",
      cardTitle:
        "If you are a HR at Corporate or part of Employee benefits platform",
      bullets: [
        "Get an intelligent EMI management platform for your employees",
        "Be the saviour and reduce financial stress of employees and increase take-home income",
      ],
      iconText:
        "If you care about your employees and their benefits and want them to be free from financial stress",
      image: "/images/Rectangle 86.svg",
    },
  ];

  const [isHidden, setIsHidden] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [isScrollLocked, setIsScrollLocked] = useState(false);
  const [sectionCompleted, setSectionCompleted] = useState("none");
  const processContentRef = useRef(null);
  const processSectionRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const lastScrollTime = useRef(0);
  const scrollAccumulator = useRef(0);
  const scrollLockPosition = useRef(0);
  const isInSection = useRef(false);

  const toggleMenuItemClick = (e) => {
    e.preventDefault();
    setIsHidden((prevState) => !prevState);
    console.log("Menu toggled, isHidden:", !isHidden); // Debug log
  };

  // Treadmill effect for process content area only (not the title)
  useEffect(() => {
    const handleWheel = (e) => {
      // Use processContentRef instead of processSectionRef
      // This targets only the content area with tabs and cards
      if (!processContentRef.current) return;

      const contentRect = processContentRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Check if the content area (tabs and cards) is in the viewport zone
      const contentTop = contentRect.top;
      const contentBottom = contentRect.bottom;

      // Content area should be taking up significant viewport space
      const contentInZone =
        contentTop < viewportHeight * 0.7 &&
        contentBottom > viewportHeight * 0.3;

      if (contentInZone) {
        // Check if we should let the page scroll
        const shouldAllowScroll =
          (e.deltaY > 0 && activeTab === tabData.length - 1) || // At last tab, scrolling down
          (e.deltaY < 0 && activeTab === 0); // At first tab, scrolling up

        if (shouldAllowScroll) {
          // Don't prevent default, let page scroll naturally
          return;
        }

        // Prevent page scroll when navigating through tabs
        e.preventDefault();
        e.stopPropagation();

        // Accumulate scroll
        scrollAccumulator.current += e.deltaY;

        // Process tab change like a treadmill
        if (Math.abs(scrollAccumulator.current) > 100) {
          const now = Date.now();
          if (now - lastScrollTime.current > 400) {
            if (e.deltaY > 0) {
              // Scrolling down - next tab
              if (activeTab < tabData.length - 1) {
                setActiveTab((prev) => prev + 1);
              }
            } else {
              // Scrolling up - previous tab
              if (activeTab > 0) {
                setActiveTab((prev) => prev - 1);
              }
            }
            lastScrollTime.current = now;
            scrollAccumulator.current = 0;
          }
        }
      }

      // Reset when content area leaves viewport completely
      if (contentRect.bottom < 0 || contentRect.top > viewportHeight) {
        setActiveTab(0);
        scrollAccumulator.current = 0;
      }
    };

    window.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      window.removeEventListener("wheel", handleWheel);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [activeTab, tabData.length]);

  // Handle manual tab click
  const handleTabClick = (index) => {
    setActiveTab(index);
    // If manually clicking while in section, maintain the lock
    if (isInSection.current) {
      scrollAccumulator.current = 0;
    }
  };

  // Handle previous/next navigation
  const handlePrevTab = () => {
    if (isInSection.current) {
      scrollAccumulator.current = 0;
    }
    setActiveTab((prev) => {
      const next = prev - 1;
      if (next < 0) return 0;
      return next;
    });
  };

  const handleNextTab = () => {
    if (isInSection.current) {
      scrollAccumulator.current = 0;
    }
    setActiveTab((prev) => {
      const next = prev + 1;
      if (next >= tabData.length) return tabData.length - 1;
      return next;
    });
  };

  return (
    <div className={`${styles.pageContainer} relative`}>
      <div
        className={`${styles.container} relative overflow-hidden flex flex-column`}
      >
        {/* Navigation */}
        <div className={`${styles.navigation} px-4 md:px-8 py-4 relative`}>
          <div className="flex align-items-center justify-content-between w-full">
            {/* Logo on far left */}
            <div className="flex align-items-center select-none">
              <img
                src={getImagePath("headerlogo2.png")}
                decoding="async"
                alt="Interesting"
                className={styles.headerLogo}
              />
            </div>

            {/* Mobile menu toggle - only visible on mobile */}
            <button
              onClick={toggleMenuItemClick}
              className="cursor-pointer block lg:hidden border-none bg-transparent p-2"
              type="button"
              aria-label="Toggle menu"
            >
              <i className={`${styles.mobileMenuIcon} pi pi-bars text-2xl`}></i>
            </button>

            {/* Desktop Navigation */}
            <div
              className={`${styles.desktopNav} hidden lg:flex align-items-center justify-content-between`}
            >
              {/* Center menu items */}
              <div className="flex align-items-center justify-content-center flex-grow-1">
                <ul className="list-none p-0 m-0 flex align-items-center select-none">
                  <li className="mx-3">
                    <a
                      href="/app/offering"
                      className={`${styles.menuItem} p-ripple flex align-items-center font-medium cursor-pointer transition-colors transition-duration-150 py-2`}
                    >
                      <span>Offering</span>
                    </a>
                  </li>
                  <li className="mx-3">
                    <a
                      href="/app/pricing"
                      className={`${styles.menuItem} p-ripple flex align-items-center font-medium cursor-pointer transition-colors transition-duration-150 py-2`}
                    >
                      <span>Pricing</span>
                    </a>
                  </li>
                  {/* <li className="mx-3">
                    <a
                      href="#why-us"
                      className={`${styles.menuItem} p-ripple flex align-items-center font-medium cursor-pointer transition-colors transition-duration-150 py-2`}
                    >
                      <span>Why us</span>
                    </a>
                  </li>
                  <li className="mx-3">
                    <a
                      href="#ting"
                      className={`${styles.menuItem} p-ripple flex align-items-center font-medium cursor-pointer transition-colors transition-duration-150 py-2`}
                    >
                      <span>Ting</span>
                    </a>
                  </li> */}
                  <li className="mx-3">
                    <a
                      href="/app/contact"
                      className={`${styles.menuItem} p-ripple flex align-items-center font-medium cursor-pointer transition-colors transition-duration-150 py-2`}
                    >
                      <span>Contact us</span>
                    </a>
                  </li>
                </ul>
              </div>

              {/* CTA button on far right */}
              <div className="flex align-items-center">
                <a
                  href="https://app.interestng.com/app/login"
                  className={styles.heroCta1}
                >
                  Login
                </a>
              </div>
            </div>

            {/* Mobile menu - only shows on mobile when toggled */}
            {!isHidden && (
              <div
                className={`${styles.mobileMenu} block lg:hidden absolute top-full left-0 w-full shadow-2`}
              >
                <ul className="list-none p-0 m-0 flex flex-column">
                  <li>
                    <a
                      href="/app/offering"
                      className={`${styles.mobileMenuItem} p-ripple flex px-4 py-3 align-items-center font-medium cursor-pointer`}
                    >
                      <span>Offering</span>
                    </a>
                  </li>
                  <li>
                    <a
                      href="/app/pricing"
                      className={`${styles.mobileMenuItem} p-ripple flex px-4 py-3 align-items-center font-medium cursor-pointer`}
                    >
                      <span>Pricing</span>
                    </a>
                  </li>
                  <li>
                    <a
                      href="#why-us"
                      className={`${styles.mobileMenuItem} p-ripple flex px-4 py-3 align-items-center font-medium cursor-pointer`}
                    >
                      <span>Why us</span>
                    </a>
                  </li>
                  {/* No #ting section exists on this page (the desktop Ting
                      item is commented out for the same reason).
                  <li>
                    <a
                      href="#ting"
                      className={`${styles.mobileMenuItem} p-ripple flex px-4 py-3 align-items-center font-medium cursor-pointer`}
                    >
                      <span>Ting</span>
                    </a>
                  </li> */}
                  <li>
                    <a
                      href="/app/contact"
                      className={`${styles.mobileMenuItem} p-ripple flex px-4 py-3 align-items-center font-medium cursor-pointer`}
                    >
                      <span>Contact us</span>
                    </a>
                  </li>
                  <li className="p-3">
                    <a
                      href="https://app.interestng.com/app/login"
                      className={styles.ctaButton2}
                    >
                      Login
                    </a>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
        {/* All Main Sections Container */}
        <div className={styles.allSectionsContainer}>
          {/* Hero Section Content */}
          <div
            className={`${styles.heroContentContainer} px-4 md:px-8 py-8 relative`}
          >
            <div className={`${styles.heroContent} text-center`}>
              {/* Logo */}
              <div className="flex align-items-center justify-content-center mb-6">
                <div
                  className={`${styles.logoContainer} flex align-items-center justify-content-center`}
                >
                  <img
                    src={getImagePath("path41 1.svg")}
                    decoding="async"
                    alt="Interesting Logo"
                    className={`${styles.logoImage} w-full h-full object-contain`}
                  />
                </div>
              </div>

              {/* Main heading */}
              <h1 className={`${styles.mainHeading} mb-4 mx-auto`}>
                You need to make loan approvals
                <br />a whole lot more{" "}
                <span className={styles.brandColor}>Interesting</span>
              </h1>

              {/* Subtitle */}
              <p className={`${styles.subtitle} mb-6 mx-auto`}>
                Tell your borrower to bring Lakshmi home.
                <br />
                Within minutes
              </p>

              {/* CTA Button */}
              <a
                href="https://app.interestng.com/app/login"
                className={styles.heroCta}
              >
                Let's get started
              </a>
            </div>
          </div>

          {/* Features Section */}
          <div id="offerings" className={`px-4 md:px-8 py-8`}>
            <div className="text-center mb-8">
              <h2 className={`${styles.featuresTitle} text-900 mb-4`}>
                Are you Interesting?
              </h2>
              <p className={`${styles.featuresSubtitle} text-color-secondary`}>
                It's about you. Do you:
              </p>
            </div>
            <div className="grid">
              <div className="col-12 md:col-4 text-center p-4">
                <div className={`${styles.featureCard} bg-white p-6 h-full`}>
                  <div
                    className={`${styles.featureIconContainer} flex align-items-center justify-content-center mx-auto mb-4`}
                  >
                    <img
                      src={getImagePath("global-partner-svgrepo-com 1.svg")}
                      loading="lazy"
                      decoding="async"
                      alt="Global Partner Icon"
                      className={styles.featureIconImage}
                    />
                  </div>
                  <h3 className={`${styles.featureTitle} text-900 mb-4`}>
                    Want to earn extra income
                    <br />
                    with simple referrals?
                  </h3>
                  <div className={`${styles.featureList} text-left`}>
                    <div
                      className={`${styles.featureItem} flex align-items-start mb-3`}
                    >
                      <img
                        src={getImagePath("Frame-1.svg")}
                        loading="lazy"
                        decoding="async"
                        alt="Check"
                        className={`${styles.checkIcon} mr-3`}
                      />
                      <span>Work in College or University Admissions</span>
                    </div>
                    <div
                      className={`${styles.featureItem} flex align-items-start mb-3`}
                    >
                      <img
                        src={getImagePath("Frame-1.svg")}
                        loading="lazy"
                        decoding="async"
                        alt="Check"
                        className={`${styles.checkIcon} mr-3`}
                      />
                      <span>
                        Distribute Tally, Zoho, or Any Other SAAS Product
                      </span>
                    </div>
                    <div
                      className={`${styles.featureItem} flex align-items-start mb-3`}
                    >
                      <img
                        src={getImagePath("Frame-1.svg")}
                        loading="lazy"
                        decoding="async"
                        alt="Check"
                        className={`${styles.checkIcon} mr-3`}
                      />
                      <span>Anyone who wants to refer</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-12 md:col-4 text-center p-4">
                <div className={`${styles.featureCard} bg-white p-6 h-full`}>
                  <div
                    className={`${styles.featureIconContainer} flex align-items-center justify-content-center mx-auto mb-4`}
                  >
                    <img
                      src={getImagePath("work-svgrepo-com 1.svg")}
                      loading="lazy"
                      decoding="async"
                      alt="Work Icon"
                      className={styles.featureIconImage}
                    />
                  </div>
                  <h3 className={`${styles.featureTitle} text-900 mb-4`}>
                    Already work in finance,
                    <br />
                    sales, or loans and want to
                    <br />
                    grow more?
                  </h3>
                  <div className={`${styles.featureList} text-left`}>
                    <div
                      className={`${styles.featureItem} flex align-items-start mb-3`}
                    >
                      <img
                        src={getImagePath("Frame-1.svg")}
                        loading="lazy"
                        decoding="async"
                        alt="Check"
                        className={`${styles.checkIcon} mr-3`}
                      />
                      <span>Direct Sales Agent (DSA)/ Loan agent</span>
                    </div>
                    <div
                      className={`${styles.featureItem} flex align-items-start mb-3`}
                    >
                      <img
                        src={getImagePath("Frame-1.svg")}
                        loading="lazy"
                        decoding="async"
                        alt="Check"
                        className={`${styles.checkIcon} mr-3`}
                      />
                      <span>Ex-Banker, Financial Consultant, Auditor</span>
                    </div>
                    <div
                      className={`${styles.featureItem} flex align-items-start mb-3`}
                    >
                      <img
                        src={getImagePath("Frame-1.svg")}
                        loading="lazy"
                        decoding="async"
                        alt="Check"
                        className={`${styles.checkIcon} mr-3`}
                      />
                      <span>Business Correspondent of Banks</span>
                    </div>
                    <div
                      className={`${styles.featureItem} flex align-items-start mb-3`}
                    >
                      <img
                        src={getImagePath("Frame-1.svg")}
                        loading="lazy"
                        decoding="async"
                        alt="Check"
                        className={`${styles.checkIcon} mr-3`}
                      />
                      <span>Dealers and real estate builders</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-12 md:col-4 text-center p-4">
                <div className={`${styles.featureCard} bg-white p-6 h-full`}>
                  <div
                    className={`${styles.featureIconContainer} flex align-items-center justify-content-center mx-auto mb-4`}
                  >
                    <img
                      src={getImagePath("rocket2-svgrepo-com 1.svg")}
                      loading="lazy"
                      decoding="async"
                      alt="Rocket Icon"
                      className={styles.featureIconImage}
                    />
                  </div>
                  <h3 className={`${styles.featureTitle} text-900 mb-4`}>
                    Run a business and want
                    <br />
                    to scale it with financial
                    <br />
                    services?
                  </h3>
                  <div className={`${styles.featureList} text-left`}>
                    <div
                      className={`${styles.featureItem} flex align-items-start mb-3`}
                    >
                      <img
                        src={getImagePath("Frame-1.svg")}
                        loading="lazy"
                        decoding="async"
                        alt="Check"
                        className={`${styles.checkIcon} mr-3`}
                      />
                      <span>Run a B2B, B2C, D2C Startup</span>
                    </div>
                    <div
                      className={`${styles.featureItem} flex align-items-start mb-3`}
                    >
                      <img
                        src={getImagePath("Frame-1.svg")}
                        loading="lazy"
                        decoding="async"
                        alt="Check"
                        className={`${styles.checkIcon} mr-3`}
                      />
                      <span>Work in HR in Corporates</span>
                    </div>
                    <div
                      className={`${styles.featureItem} flex align-items-start mb-3`}
                    >
                      <img
                        src={getImagePath("Frame-1.svg")}
                        loading="lazy"
                        decoding="async"
                        alt="Check"
                        className={`${styles.checkIcon} mr-3`}
                      />
                      <span>Employee Rewards/Expense management platform</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Process Section */}
          <div
            ref={processSectionRef}
            className={`px-4 md:px-8 py-8 relative`}
            style={{ overflow: "hidden" }}
          >
            {/* Background Mesh */}
            <div
              className="absolute"
              style={{
                top: "20%",
                right: "-50px",
                transform: "translateY(-50%)",
                width: "1000px",
                height: "2000px",
                zIndex: 0,
              }}
            >
              <img
                src={getImagePath("Group.png")}
                loading="lazy"
                decoding="async"
                alt="Decorative Mesh"
                className="w-full h-full object-contain"
              />
            </div>

            <div className={`${styles.processContent} text-center mb-6`}>
              <h2 className={`${styles.processTitle} text-900 mb-4`}>
                Here's how you can be more Interesting
              </h2>
              <p
                className={`${styles.processSubtitle} text-color-secondary mx-auto`}
              >
                No matter where you are in your journey, casual
                <br />
                referral or scaling big, its easy to get started
              </p>
            </div>

            <div
              ref={processContentRef}
              className={`${styles.processContent} flex align-items-start mt-8`}
              id="process-content"
              style={{ transition: "opacity 0.3s ease-in-out" }}
            >
              <div className={`${styles.leftSection} flex-1`}>
                <div className="text-left">
                  <div className={`${styles.timelineSteps} mt-6`}>
                    {tabData.map((tab, index) => (
                      <div
                        key={index}
                        className={`${styles.timelineStep} ${
                          index === activeTab ? styles.activeTimelineStep : ""
                        }`}
                        onClick={() => handleTabClick(index)}
                      >
                        <div className={styles.timelineStepContent}>
                          <h4 className={styles.timelineStepTitle}>
                            {tab.title}
                          </h4>
                          {index === activeTab && (
                            <p
                              className={
                                styles.processCardSubtitleWithUnderline
                              }
                            >
                              {tab.subtitle
                                .split("\n")
                                .map((line, lineIndex) => (
                                  <span key={lineIndex}>
                                    {line}
                                    {lineIndex <
                                      tab.subtitle.split("\n").length - 1 && (
                                      <br />
                                    )}
                                  </span>
                                ))}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className={`${styles.centerIndicator}`}>
                {tabData.map((tab, index) => (
                  <div
                    key={index}
                    className={`${styles.centerDot} ${
                      index === activeTab ? styles.activeCenterDot : ""
                    }`}
                    onClick={() => handleTabClick(index)}
                  ></div>
                ))}
              </div>
              <div className={`${styles.rightSection} flex-1`}>
                <div className={`${styles.rightSideCard} p-6`}>
                  <h4 className={styles.cardTitle}>
                    {tabData[activeTab].cardTitle}
                  </h4>
                  <ul className={`${styles.cardBulletList} mb-6`}>
                    {tabData[activeTab].bullets.map((bullet, index) => (
                      <li key={index}>{bullet}</li>
                    ))}
                  </ul>
                  <div
                    className={`${styles.iconSection} flex align-items-center mb-4`}
                  >
                    <div className={`${styles.lightbulbIcon} mr-4`}>
                      <img
                        src={getImagePath("bulp-lamp-on-svgrepo-com 1.svg")}
                        loading="lazy"
                        decoding="async"
                        alt="Lightbulb"
                        className={styles.lightbulbImage}
                      />
                    </div>
                    <p className={styles.iconText}>
                      {tabData[activeTab].iconText}
                    </p>
                  </div>

                  {/* Manual Navigation Controls */}
                  <div className="flex align-items-center justify-content-between mt-4">
                    <button
                      onClick={handlePrevTab}
                      className="p-button-text p-button-secondary"
                      aria-label="Previous tab"
                      style={{ fontSize: "14px" }}
                    >
                      ← Previous
                    </button>
                    <span style={{ fontSize: "14px", color: "#6B7280" }}>
                      {activeTab + 1} / {tabData.length}
                    </span>
                    <button
                      onClick={handleNextTab}
                      className="p-button-text p-button-secondary"
                      aria-label="Next tab"
                      style={{ fontSize: "14px" }}
                    >
                      Next →
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className={`${styles.processContent} text-center mt-8`}>
              <a href="/app/offering" className={styles.heroCta}>
                Explore our Offering
              </a>
            </div>
          </div>

          {/* Why Interesting Section */}
          <div
            id="why-us"
            className={`px-4 md:px-8 py-8`}
            style={{ position: "relative" }}
          >
            <div className={styles.sectionLeftDecoration}>
              <img
                src={getImagePath("Group1000004786.png")}
                loading="lazy"
                decoding="async"
                alt="Section Decoration"
                className={styles.sectionLeftDecorationImage}
              />
            </div>
            <div className="text-center mb-8">
              <h2 className={`${styles.processTitle} text-900 mb-4`}>
                Why Interesting
              </h2>
              <p
                className={`${styles.processSubtitle} text-color-secondary mx-auto`}
              >
                You Bring the Relationship, We Bring the Tech.
                <br />
                Together, we Close Faster
              </p>
            </div>

            <div className="grid">
              <div className="col-12 md:col-4 text-left p-4">
                <div className={`${styles.whyUsCard} p-6`}>
                  <div className={styles.cardTopRightIcon}>
                    <img
                      src={getImagePath("path3653 3-1.svg")}
                      loading="lazy"
                      decoding="async"
                      alt="Decorator"
                      className={styles.topRightIconImage}
                    />
                  </div>
                  <div
                    className={`${styles.whyUsIcon1} flex align-items-center justify-content-start mb-4`}
                  >
                    <img
                      src={getImagePath("growth-svgrepo-com 1.svg")}
                      loading="lazy"
                      decoding="async"
                      alt="Growth"
                      className={styles.whyUsIconImage}
                    />
                  </div>
                  <h3 className={`${styles.whyUsItemTitle} mb-3`}>
                    You Earn Additional Income
                  </h3>
                  <p className={`${styles.whyUsItemDescription}`}>
                    Turn every loan disbursal into real earnings with the
                    Interestng platform
                  </p>
                </div>
              </div>
              <div className="col-12 md:col-4 text-left p-4">
                <div className={`${styles.whyUsCard} p-6`}>
                  <div className={styles.cardTopRightIcon}>
                    <img
                      src={getImagePath("path3653 3-1.svg")}
                      loading="lazy"
                      decoding="async"
                      alt="Decorator"
                      className={styles.topRightIconImage}
                    />
                  </div>
                  <div
                    className={`${styles.whyUsIcon2} flex align-items-center justify-content-start mb-4`}
                  >
                    <img
                      src={getImagePath("piggy-bank-save-svgrepo-com 1.svg")}
                      loading="lazy"
                      decoding="async"
                      alt="Piggy Bank Save"
                      className={styles.whyUsIconImage}
                    />
                  </div>
                  <h3 className={`${styles.whyUsItemTitle} mb-3`}>
                    Borrowers Save Money
                  </h3>
                  <p className={`${styles.whyUsItemDescription}`}>
                    Our "Borrower First" approach offers multiple options for
                    saving interest cost. This helps you be more successful
                  </p>
                </div>
              </div>
              <div className="col-12 md:col-4 text-left p-4">
                <div className={`${styles.whyUsCard} p-6`}>
                  <div className={styles.cardTopRightIcon}>
                    <img
                      src={getImagePath("path3653 3-1.svg")}
                      loading="lazy"
                      decoding="async"
                      alt="Decorator"
                      className={styles.topRightIconImage}
                    />
                  </div>
                  <div
                    className={`${styles.whyUsIcon3} flex align-items-center justify-content-start mb-4`}
                  >
                    <img
                      src={getImagePath("goal-success-svgrepo-com 1.svg")}
                      loading="lazy"
                      decoding="async"
                      alt="Goal Success"
                      className={styles.whyUsIconImage}
                    />
                  </div>
                  <h3 className={`${styles.whyUsItemTitle} mb-3`}>
                    Reduce Your Bank Friction for Deals
                  </h3>
                  <p className={`${styles.whyUsItemDescription}`}>
                    So you can focus on succeeding with more loan approvals
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center mt-8">
              <a href="/app/pricing" className={styles.heroCta}>
                Explore our Pricing
              </a>
            </div>
          </div>

          {/* Statistics Section */}
          <div className={`px-4 md:px-8 py-8 relative overflow-hidden`}>
            <img
              src={getImagePath("Group1000004785.png")}
              loading="lazy"
              decoding="async"
              alt="Statistics Background Shape"
              className="absolute top-0 z-0"
              style={{
                right: "0",
                width: "35%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "left center",
              }}
            />
            <div className="grid align-items-center">
              <div className="col-12 md:col-6 mb-6 md:mb-0">
                <h2 className={`${styles.statsTitle} text-900 mb-4`}>
                  About Us
                </h2>
                <p className={`${styles.aboutUsSubtitle} mb-4`}>
                  8 Years of Bringing Confidence to Our Users
                </p>
                <p className={`${styles.aboutUsDescription}`}>
                  At Interesting, we're reimagining how loans get done,
                  <br />
                  whether you're just curious, already hustling
                  <br />
                  in the loan space, or ready to build a serious
                  <br />
                  business, we give you the tools, knowledge,
                  <br />
                  and connections to make it happen.
                </p>
              </div>
              <div className="col-12 md:col-6">
                <div className="grid">
                  <div className="col-6">
                    <div className={styles.statCard}>
                      <h3 className={`${styles.statNumber}`}>500 +</h3>
                      <p className={`${styles.statDescription}`}>
                        Active Agents across India
                      </p>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className={styles.statCard}>
                      <h3 className={`${styles.statNumber}`}>30 +</h3>
                      <p className={`${styles.statDescription}`}>
                        Public and Private Banks, NBFCs and fintechs
                      </p>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className={styles.statCard}>
                      <h3 className={`${styles.statNumber}`}>2,000cr</h3>
                      <p className={`${styles.statDescription}`}>
                        Loan disbursals
                      </p>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className={styles.statCard}>
                      <h3 className={`${styles.statNumber}`}>10</h3>
                      <p className={`${styles.statDescription}`}>
                        Loan Products
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Blog Section */}
          <div className={`${styles.blogSection} px-4 md:px-8 py-8`}>
            <div className={styles.blogLeftDecoration}>
              <img
                src={getImagePath("logoWhite.svg")}
                loading="lazy"
                decoding="async"
                alt="Logo Decoration"
                className={styles.blogLeftDecorationImage}
              />
            </div>
            <div className="text-center mb-8">
              <h2 className={styles.blogTitle}>Ting</h2>
              <p className={styles.blogSubtitle}>
                Thought-Leadership. Insights. Numbers. Growth
                <br />
                The Ting blog is a fun read
              </p>
            </div>

            <div className="grid justify-content-center">
              <div className="col-12 md:col-6 mb-4 flex justify-content-center">
                <div className={styles.blogCard}>
                  <div className={`${styles.blogImageContainer} relative`}>
                    <picture>
                      <source
                        srcSet={getImagePath("Trading.webp")}
                        type="image/webp"
                      />
                      <img
                        src={getImagePath("Trading.jpg")}
                        loading="lazy"
                        decoding="async"
                        width={1116}
                        height={620}
                        alt="Trading"
                        className={`${styles.blogCardImage} w-full h-full object-cover`}
                      />
                    </picture>
                  </div>
                  <div className="p-3">
                    <div className="flex align-items-center mb-3">
                      <span className={styles.blogTag}>Blog</span>
                    </div>
                    <h3 className={`${styles.blogCardTitle} text-900 mb-3`}>
                      No 1 reason you lose a loan deal
                    </h3>
                    <p className={styles.blogCardDescription}>
                      Most deals fail not because of effort, but because of
                      missing the right process. Here's how to fix it.
                    </p>
                    <div className="flex align-items-center mt-3">
                      <img
                        src={getImagePath("Frame.svg")}
                        loading="lazy"
                        decoding="async"
                        alt="Clock"
                        className="mr-2"
                        width={14}
                        height={14}
                      />
                      <span className={styles.blogReadTime}>6 min read</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-12 md:col-6 mb-4 flex justify-content-center">
                <div className={styles.blogCard}>
                  <div className={`${styles.blogImageContainer} relative`}>
                    <picture>
                      <source
                        srcSet={getImagePath("blog.webp")}
                        type="image/webp"
                      />
                      <img
                        src={getImagePath("blog.jpg")}
                        loading="lazy"
                        decoding="async"
                        width={1116}
                        height={733}
                        alt="Collaboration"
                        className={`${styles.blogCardImage} w-full h-full object-cover`}
                      />
                    </picture>
                  </div>
                  <div className="p-3">
                    <div className="flex align-items-center mb-3">
                      <span className={styles.blogTag}>Blog</span>
                    </div>
                    <h3 className={`${styles.blogCardTitle} text-900 mb-3`}>
                      Borrower doesn't give me a chance to listen to me
                    </h3>
                    <p className={styles.blogCardDescription}>
                      Most deals fail not because of effort, but because of
                      missing the right process. Here's how to fix it.
                    </p>
                    <div className="flex align-items-center mt-3">
                      <img
                        src={getImagePath("Frame.svg")}
                        loading="lazy"
                        decoding="async"
                        alt="Clock"
                        className="mr-2"
                        width={14}
                        height={14}
                      />
                      <span className={styles.blogReadTime}>6 min read</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Testimonials Section */}
          <div className={`${styles.testimonialsSection} px-4 md:px-8 py-8`}>
            <div className="text-center mb-8">
              <h2 className={`${styles.testimonialsTitle} mb-8 mx-auto`}>
                How Helping Borrowers{" "}
                <span className={styles.testimonialsHighlight}>
                  Become More
                  <br />
                  Interesting For Our Users
                </span>
              </h2>
            </div>

            <div className="grid justify-content-center">
              <div className="col-12 md:col-6 p-3 flex justify-content-center">
                <div className={styles.testimonialCard}>
                  <div className={styles.testimonialHeader}>
                    <div className={styles.testimonialStars}>
                      <img
                        src={getImagePath("Frame.png")}
                        loading="lazy"
                        decoding="async"
                        alt="Star"
                        width={20}
                        height={20}
                      />
                      <img
                        src={getImagePath("Frame.png")}
                        loading="lazy"
                        decoding="async"
                        alt="Star"
                        width={20}
                        height={20}
                      />
                      <img
                        src={getImagePath("Frame.png")}
                        loading="lazy"
                        decoding="async"
                        alt="Star"
                        width={20}
                        height={20}
                      />
                      <img
                        src={getImagePath("Frame.png")}
                        loading="lazy"
                        decoding="async"
                        alt="Star"
                        width={20}
                        height={20}
                      />
                      <img
                        src={getImagePath("StarFrame.svg")}
                        loading="lazy"
                        decoding="async"
                        alt="Star"
                        width={20}
                        height={20}
                        style={{ opacity: 0.3 }}
                      />
                    </div>
                    <div className={styles.testimonialProfileContainer}>
                      <img
                        src={getImagePath("men.svg")}
                        loading="lazy"
                        decoding="async"
                        alt="Rahul Verma"
                        className={styles.testimonialProfileImage}
                      />
                    </div>
                    <div className={styles.testimonialUserInfo}>
                      <h4 className={styles.testimonialName}>Rahul Verma</h4>
                      <p className={styles.testimonialRole}>Loan agent</p>
                    </div>
                  </div>
                  <div className={styles.testimonialContent}>
                    <div className={styles.testimonialQuote}>
                      <img
                        src={getImagePath("Vector.svg")}
                        loading="lazy"
                        decoding="async"
                        alt="Quote"
                        className={styles.quoteImage}
                      />
                    </div>
                    <p className={styles.testimonialText}>
                      Loan processing is now at my fingertips — no more chasing
                      banks or digging through spreadsheets"
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-12 md:col-5 p-3">
                <div className={styles.testimonialCard}>
                  <div className={styles.testimonialHeader}>
                    <div className={styles.testimonialStars}>
                      <img
                        src={getImagePath("Frame.png")}
                        loading="lazy"
                        decoding="async"
                        alt="Star"
                        width={20}
                        height={20}
                      />
                      <img
                        src={getImagePath("Frame.png")}
                        loading="lazy"
                        decoding="async"
                        alt="Star"
                        width={20}
                        height={20}
                      />
                      <img
                        src={getImagePath("Frame.png")}
                        loading="lazy"
                        decoding="async"
                        alt="Star"
                        width={20}
                        height={20}
                      />
                      <img
                        src={getImagePath("Frame.png")}
                        loading="lazy"
                        decoding="async"
                        alt="Star"
                        width={20}
                        height={20}
                      />
                      <img
                        src={getImagePath("StarFrame.svg")}
                        loading="lazy"
                        decoding="async"
                        alt="Star"
                        width={20}
                        height={20}
                        style={{ opacity: 0.3 }}
                      />
                    </div>
                    <div className={styles.testimonialProfileContainer}>
                      <img
                        src={getImagePath("girl.svg")}
                        loading="lazy"
                        decoding="async"
                        alt="Isha Gautam"
                        className={styles.testimonialProfileImage}
                      />
                    </div>
                    <div className={styles.testimonialUserInfo}>
                      <h4 className={styles.testimonialName}>Isha Gautam</h4>
                      <p className={styles.testimonialRole}>Loan agent</p>
                    </div>
                  </div>
                  <div className={styles.testimonialContent}>
                    <div className={styles.testimonialQuote}>
                      <img
                        src={getImagePath("Vector.svg")}
                        loading="lazy"
                        decoding="async"
                        alt="Quote"
                        className={styles.quoteImage}
                      />
                    </div>
                    <p className={styles.testimonialText}>
                      Interesting helped me shift from rate-based selling to
                      value-led conversations. My approval rates and confidence
                      both went up."
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-content-center mt-6">
              <div
                className={`${styles.paginationDots} flex align-items-center`}
              >
                <div
                  className={`${styles.paginationDot} ${styles.activeDot}`}
                ></div>
                <div className={styles.paginationDot}></div>
                <div className={styles.paginationDot}></div>
                <div className={styles.paginationDot}></div>
              </div>
            </div>
          </div>

          {/* Final CTA Section */}
          <div className={`${styles.finalCtaSection} relative`}>
            <img
              src={getImagePath("Cover.png")}
              loading="lazy"
              decoding="async"
              alt="Cover"
              className="absolute top-4 left-4"
              style={{ width: "200px", height: "auto", zIndex: 0 }}
            />
            <img
              src={getImagePath("Group1000004784.png")}
              loading="lazy"
              decoding="async"
              alt="CTA Background Shape"
              className="absolute top-0 right-0"
              style={{ width: "70%", height: "100%", zIndex: 0 }}
            />
            <div
              className={`${styles.ctaContent} px-4 md:px-8 py-8 text-center`}
              style={{ position: "relative", zIndex: 10 }}
            >
              <h2 className={`${styles.ctaTitle} mb-4 mx-auto`}>
                Let's Stop Losing Deals Together
              </h2>
              <p className={`${styles.ctaSubtitle} mb-4 mx-auto`}>
                You're already doing the hard work.
                <br />
                Now join Interesting to win smarter, faster,
                <br />
                and more often.
              </p>
              <a
                href="https://app.interestng.com/app/login"
                className={styles.ctaButton}
              >
                Login
              </a>
              <div
                className={`${styles.ctaAgents} flex align-items-center justify-content-center mt-4`}
              >
                <div className={styles.agentAvatars}>
                  <img
                    src={getImagePath("join1.png")}
                    loading="lazy"
                    decoding="async"
                    alt="Agent"
                    className={styles.agentAvatar}
                  />
                  <img
                    src={getImagePath("join2.png")}
                    loading="lazy"
                    decoding="async"
                    alt="Agent"
                    className={styles.agentAvatar}
                  />
                  <img
                    src={getImagePath("join3.png")}
                    loading="lazy"
                    decoding="async"
                    alt="Agent"
                    className={styles.agentAvatar}
                  />
                </div>
                <p className={`${styles.agentText} ml-3`}>
                  Join 500+ successful agents
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* End of All Main Sections Container */}
        {/* Footer */}
        <footer className={styles.footer}>
          <div className="px-4 md:px-8 py-8">
            <div className="grid">
              <div className="col-12 md:col-6">
                <div className="mb-6">
                  <h3 className={styles.footerBrand}>
                    Interestng™ — Life is Dull
                    <br />
                    When You Lose Deals
                  </h3>
                  <div className={styles.footerLogoContainer}>
                    <img
                      src={getImagePath("footerLogo.svg")}
                      loading="lazy"
                      decoding="async"
                      alt="Interesting Logo"
                      className={styles.footerLogoImage}
                    />
                  </div>
                </div>
              </div>
              <div className="col-12 md:col-6">
                <div className="grid">
                  <div className="col-4">
                    <ul className="list-none p-0 m-0">
                      <li className="mb-3">
                        <a
                          href="/app"
                          className={styles.footerLink}
                        >
                          Home
                        </a>
                      </li>
                      <li className="mb-3">
                        <a href="/app/offering" className={styles.footerLink}>
                          Offering
                        </a>
                      </li>
                      <li className="mb-3">
                        <a href="/app/pricing" className={styles.footerLink}>
                          Pricing
                        </a>
                      </li>
                      {/* <li className="mb-3">
                        <a href="#why-us" className={styles.footerLink}>
                          Why Us
                        </a>
                      </li> */}

                      <li className="mb-3">
                        <a href="#offerings" className={styles.footerLink}>
                          Service
                        </a>
                      </li>

                      {/* No #faq section exists on this page yet — link kept
                          out until one does, a dead anchor is worse than none.
                      <li className="mb-3">
                        <a href="#faq" className={styles.footerLink}>
                          FAQ
                        </a>
                      </li> */}

                      {/* <li className="mb-3">
                        <a href="#ting" className={styles.footerLink}>
                          Ting
                        </a>
                      </li> */}
                    </ul>
                  </div>
                  <div className="col-4">
                    <ul className="list-none p-0 m-0">
                      {/* <li className="mb-3">
                        <a href="#faq" className={styles.footerLink}>
                          FAQ
                        </a>
                      </li> */}
                      <li className="mb-3">
                        <a href="/app/about" className={styles.footerLink}>
                          About Us
                        </a>
                      </li>
                      <li className="mb-3">
                        <a href="/app/contact" className={styles.footerLink}>
                          Contact us
                        </a>
                      </li>

                      <li className="mb-3">
                        <a
                          href="/app/terms-and-conditions"
                          className={styles.footerLink}
                        >
                          Terms & Conditions
                        </a>
                      </li>
                      <li className="mb-3">
                        <a
                          href="/app/privacy-policy"
                          className={styles.footerLink}
                        >
                          Privacy Policy
                        </a>
                      </li>
                      <li className="mb-3">
                        <a
                          href="/app/refund-policy"
                          className={styles.footerLink}
                        >
                          Refund-Policy
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default InterestingLandingPage;
